import { useEffect, useRef } from "react";

import { notificationsSocketService } from "@/modules/dashboard/notifications/services/notifications-socket.service";

/**
 * A purchase is reported in two phases and a declined one may be retried at once, so pushes come in bursts:
 * one refresh after the burst instead of one per push.
 */
const LIVE_REFRESH_DEBOUNCE_MS = 400;

/**
 * Calls `onActivity` when something happened on this card while its screen is open, with the ids of the card
 * movements it was about (when the push names them):
 *
 * - A purchase approved or declined, a refund or a reversal (`card:activity`, the push behind the corner
 *   alerts);
 * - Any other balance change (`card:balance_updated`): a load or a withdrawal, a move between cards, a deposit
 *   on chain — crypto, or pesos delivered on Polygon, which lands minutes after the load was placed. Wherever
 *   it was started: this screen, another tab, the public API.
 *
 * Also called, with no ids, when the socket comes back after a drop: nothing pushed during the gap is
 * replayed, so the screen reads again.
 */
export function useCardLiveActivity(
  cardId: string | undefined,
  onActivity: (movementIds: ReadonlySet<string>) => void,
): void {
  const onActivityRef = useRef(onActivity);
  useEffect(() => {
    onActivityRef.current = onActivity;
  });

  useEffect(() => {
    if (!cardId) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let pending = new Set<string>();

    const schedule = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        const ids = pending;
        pending = new Set();
        onActivityRef.current(ids);
      }, LIVE_REFRESH_DEBOUNCE_MS);
    };

    const offActivity = notificationsSocketService.onCardActivity((activity) => {
      if (activity.card_id !== cardId) return;
      pending.add(activity.id);
      schedule();
    });
    const offBalance = notificationsSocketService.onCardBalanceUpdate((update) => {
      if (update.card_id !== cardId) return;
      if (update.card_transaction_id) pending.add(update.card_transaction_id);
      schedule();
    });
    const offReconnect = notificationsSocketService.onReconnect(schedule);

    return () => {
      offActivity();
      offBalance();
      offReconnect();
      if (timer) clearTimeout(timer);
    };
  }, [cardId]);
}
